import { signal } from "@preact/signals";
import {
  ATK_PER_LEVEL,
  BASE_WEAPON_ATK,
  CRITICAL_MULTIPLIER,
  FLEE_COST,
  GOLD_LOSS_ON_DEFEAT,
  QUICK_ANSWER_FRACTION,
  QUICK_MULTIPLIER,
  STREAK_FOR_CRITICAL,
  XP_PER_CORRECT,
} from "@/config";
import { getItem } from "@/content/items";
import { MONSTERS, type MonsterDef } from "@/content/monsters";
import {
  accuracyOf,
  checkAnswer,
  createRng,
  generateQuestion,
  numStr,
  pickTier,
  pickTopic,
  randomSeed,
  updateStat,
  type LocalizedText,
  type Question,
} from "@/math";
import { onBattleAnswer, onMonsterDefeated } from "@/quests/QuestController";
import {
  applyXp,
  hero,
  heroStats,
  heroTopics,
  itemCount,
  recordAttempt,
  removeItem,
  settings,
} from "@/state/store";

export type BattlePhase = "question" | "feedback" | "won" | "lost" | "fled";

export interface BattleFeedback {
  kind: "answer" | "potion";
  correct: boolean;
  timedOut: boolean;
  damageToMonster: number;
  damageToHero: number;
  healed: number;
  quick: boolean;
  critical: boolean;
  explanation: { pl: string[]; en: string[] };
  correctAnswerText: LocalizedText;
}

export interface BattleRewards {
  xp: number;
  gold: number;
  leveledUp: boolean;
  newLevel: number;
}

export interface BattleState {
  monster: MonsterDef;
  monsterHp: number;
  heroHp: number;
  heroHpMax: number;
  phase: BattlePhase;
  question: Question | null;
  questionStartedAt: number;
  timerMs: number;
  feedback: BattleFeedback | null;
  streak: number;
  turns: number;
  correctAnswers: number;
  rewards: BattleRewards | null;
  /** Free hints left this battle (from equipment). */
  freeHints: number;
  /** Indices of multiple-choice answers removed by a hint. */
  eliminated: number[];
  /** First solution step revealed by a hint (typed questions). */
  hintText: LocalizedText | null;
  /** Set when the battle guards a treasure chest. */
  chestId: string | null;
  /** Counters the scene watches to trigger hit animations. */
  monsterFlash: number;
  heroFlash: number;
}

export interface BattleResult {
  won: boolean;
  fled: boolean;
  monsterId: string;
  chestId: string | null;
}

export const battle = signal<BattleState | null>(null);

let lastResult: BattleResult | null = null;

export function getLastBattleResult(): BattleResult | null {
  return lastResult;
}

export function startBattle(monsterId: string, context: { chestId?: string } = {}): void {
  const monster = MONSTERS[monsterId];
  if (!monster) throw new Error(`Unknown monster: ${monsterId}`);
  const h = hero.value;
  const stats = heroStats(h);
  const timerSeconds = settings.value.timerSeconds;
  const state: BattleState = {
    monster,
    monsterHp: monster.hp,
    heroHp: h.hp,
    heroHpMax: h.hpMax,
    phase: "question",
    question: null,
    questionStartedAt: 0,
    timerMs: timerSeconds > 0 ? (timerSeconds + stats.timerBonusSec) * 1000 : 0,
    feedback: null,
    streak: h.streak,
    turns: 0,
    correctAnswers: 0,
    rewards: null,
    freeHints: stats.freeHints,
    eliminated: [],
    hintText: null,
    chestId: context.chestId ?? null,
    monsterFlash: 0,
    heroFlash: 0,
  };
  battle.value = withNextQuestion(state);
}

function withNextQuestion(s: BattleState): BattleState {
  const h = hero.value;
  const rng = createRng(randomSeed());
  const topics = heroTopics(h);
  const topicId = pickTopic({ topics, stats: h.stats, recent: h.recentTopics, rng });
  const tier = pickTier(s.monster.rank, accuracyOf(h.stats[topicId]), rng);
  const question = generateQuestion(topicId, tier, randomSeed(), settings.value.preference);
  return {
    ...s,
    phase: "question",
    question,
    questionStartedAt: performance.now(),
    feedback: null,
    eliminated: [],
    hintText: null,
  };
}

export function answerText(q: Question): LocalizedText {
  const a = q.answer;
  switch (a.mode) {
    case "numeric": {
      const sep = (u: string) => (u === "°" || u === "" ? "" : " ");
      const unitPl = a.unit ? sep(a.unit.pl) + a.unit.pl : "";
      const unitEn = a.unit ? sep(a.unit.en) + a.unit.en : "";
      return { pl: numStr(a.value, "pl") + unitPl, en: numStr(a.value, "en") + unitEn };
    }
    case "fraction":
      return { pl: `${a.num}/${a.den}`, en: `${a.num}/${a.den}` };
    case "choice4":
      return a.choices[a.correctIndex] ?? { pl: "?", en: "?" };
    case "numericSet":
      return {
        pl: a.values.map((v) => numStr(v, "pl")).join("; "),
        en: a.values.map((v) => numStr(v, "en")).join("; "),
      };
    case "truefalse":
      return a.value ? { pl: "Prawda", en: "True" } : { pl: "Fałsz", en: "False" };
    case "text":
      return { pl: a.value, en: a.value };
  }
}

/** What the player typed or clicked, as text for the report. */
export function givenText(q: Question, input: string | number | null): string {
  if (input === null) return "";
  if (q.answer.mode === "choice4" && typeof input === "number")
    return q.answer.choices[input]?.pl ?? String(input);
  return String(input);
}

export function answerCurrent(input: string | number): void {
  resolveTurn(input, false);
}

export function timeoutCurrent(): void {
  resolveTurn(null, true);
}

function resolveTurn(input: string | number | null, timedOut: boolean): void {
  const s = battle.value;
  if (!s || s.phase !== "question" || !s.question) return;
  const q = s.question;
  const elapsed = performance.now() - s.questionStartedAt;
  const correct = !timedOut && input !== null && checkAnswer(q.answer, input);
  const rng = createRng(randomSeed());
  const h = hero.value;
  const gear = heroStats(h);

  const stats = { ...h.stats, [q.topicId]: updateStat(h.stats[q.topicId], correct, Math.round(elapsed)) };
  const recentTopics = [...h.recentTopics, q.topicId].slice(-5);
  const streak = correct ? s.streak + 1 : 0;

  let damageToMonster = 0;
  let damageToHero = 0;
  let quick = false;
  let critical = false;
  if (correct) {
    let dmg = BASE_WEAPON_ATK + h.lvl * ATK_PER_LEVEL + gear.atk;
    quick = s.timerMs > 0 && elapsed < s.timerMs * QUICK_ANSWER_FRACTION;
    if (quick) dmg *= QUICK_MULTIPLIER;
    critical = streak > 0 && streak % STREAK_FOR_CRITICAL === 0;
    if (critical) dmg *= CRITICAL_MULTIPLIER;
    damageToMonster = Math.max(1, Math.round(dmg) - s.monster.def);
  } else {
    damageToHero = Math.max(1, s.monster.atk + rng.int(-1, 1) - gear.def);
  }

  const monsterHp = Math.max(0, s.monsterHp - damageToMonster);
  const heroHp = Math.max(0, s.heroHp - damageToHero);

  hero.value = recordAttempt(
    {
      ...h,
      stats,
      recentTopics,
      streak,
      bestStreak: Math.max(h.bestStreak, streak),
      answered: h.answered + 1,
      correct: h.correct + (correct ? 1 : 0),
      xp: h.xp + (correct ? XP_PER_CORRECT : 0),
      hp: heroHp,
    },
    {
      at: new Date().toISOString(),
      topicId: q.topicId,
      tier: q.tier,
      prompt: q.prompt.pl,
      answer: answerText(q).pl,
      given: givenText(q, input),
      correct,
      timeMs: Math.round(elapsed),
      context: "battle",
    },
  );

  onBattleAnswer(correct, streak);

  battle.value = {
    ...s,
    monsterHp,
    heroHp,
    streak,
    phase: "feedback",
    turns: s.turns + 1,
    correctAnswers: s.correctAnswers + (correct ? 1 : 0),
    monsterFlash: s.monsterFlash + (correct ? 1 : 0),
    heroFlash: s.heroFlash + (correct ? 0 : 1),
    feedback: {
      kind: "answer",
      correct,
      timedOut,
      damageToMonster,
      damageToHero,
      healed: 0,
      quick,
      critical,
      explanation: q.explanation,
      correctAnswerText: answerText(q),
    },
  };
}

/** Drink a potion (takes the turn: the monster attacks afterwards). */
export function usePotion(itemId: string): void {
  const s = battle.value;
  const h = hero.value;
  const item = getItem(itemId);
  if (!s || s.phase !== "question" || !item.heal || itemCount(h, itemId) <= 0 || s.heroHp >= s.heroHpMax)
    return;
  const without = removeItem(h, itemId);
  if (!without) return;
  const rng = createRng(randomSeed());
  const healed = Math.min(item.heal, s.heroHpMax - s.heroHp);
  const damageToHero = Math.max(1, s.monster.atk + rng.int(-1, 1) - heroStats(h).def);
  const heroHp = Math.max(0, s.heroHp + healed - damageToHero);
  hero.value = { ...without, hp: heroHp };
  battle.value = {
    ...s,
    heroHp,
    phase: "feedback",
    turns: s.turns + 1,
    heroFlash: s.heroFlash + 1,
    feedback: {
      kind: "potion",
      correct: false,
      timedOut: false,
      damageToMonster: 0,
      damageToHero,
      healed,
      quick: false,
      critical: false,
      explanation: { pl: [], en: [] },
      correctAnswerText: { pl: "", en: "" },
    },
  };
}

/** Use the first potion the hero owns (keyboard shortcut). */
export function useAnyPotion(): void {
  const h = hero.value;
  const id = ["potion", "big_potion"].find((i) => itemCount(h, i) > 0);
  if (id) usePotion(id);
}

/** Whether a hint can be used right now (free from equipment or a scroll in the bag). */
export function hintAvailable(): { free: boolean; scroll: boolean } {
  const s = battle.value;
  const h = hero.value;
  const alreadyUsed = !!s && (s.eliminated.length > 0 || s.hintText !== null);
  if (!s || s.phase !== "question" || alreadyUsed) return { free: false, scroll: false };
  return { free: s.freeHints > 0, scroll: itemCount(h, "hint_scroll") > 0 };
}

/** Work out what a hint reveals for a question: two wrong choices, or the first solution step. */
export function computeHint(q: Question): { eliminated: number[]; hintText: LocalizedText | null } {
  const answer = q.answer;
  const rng = createRng(randomSeed());
  if (answer.mode === "choice4") {
    const wrong = [0, 1, 2, 3].filter((i) => i !== answer.correctIndex);
    return { eliminated: rng.shuffle(wrong).slice(0, 2), hintText: null };
  }
  return { eliminated: [], hintText: { pl: q.explanation.pl[0] ?? "", en: q.explanation.en[0] ?? "" } };
}

/** Remove two wrong choices, or reveal the first solution step. Does not take the turn. */
export function useHint(): void {
  const s = battle.value;
  if (!s || !s.question) return;
  const avail = hintAvailable();
  if (!avail.free && !avail.scroll) return;
  if (!avail.free) {
    const without = removeItem(hero.value, "hint_scroll");
    if (!without) return;
    hero.value = without;
  }
  const { eliminated, hintText } = computeHint(s.question);
  battle.value = { ...s, freeHints: avail.free ? s.freeHints - 1 : s.freeHints, eliminated, hintText };
}

export function continueBattle(): void {
  const s = battle.value;
  if (!s || s.phase !== "feedback") return;
  if (s.monsterHp <= 0) {
    const h = hero.value;
    const defeated = { ...h.defeated, [s.monster.id]: (h.defeated[s.monster.id] ?? 0) + 1 };
    const { hero: h2, leveledUp } = applyXp({ ...h, gold: h.gold + s.monster.gold, defeated }, s.monster.xp);
    hero.value = h2;
    onMonsterDefeated(s.monster.id);
    battle.value = {
      ...s,
      phase: "won",
      rewards: {
        xp: s.monster.xp + s.correctAnswers * XP_PER_CORRECT,
        gold: s.monster.gold,
        leveledUp,
        newLevel: h2.lvl,
      },
    };
    return;
  }
  if (s.heroHp <= 0) {
    const h = hero.value;
    const lost = Math.floor(h.gold * GOLD_LOSS_ON_DEFEAT);
    hero.value = { ...h, gold: h.gold - lost, hp: h.hpMax, streak: 0 };
    battle.value = {
      ...s,
      phase: "lost",
      rewards: { xp: 0, gold: -lost, leveledUp: false, newLevel: h.lvl },
    };
    return;
  }
  battle.value = withNextQuestion(s);
}

/** Run away: allowed against anything but bosses, costs a few gold, the monster stays on the map. */
export function fleeBattle(): void {
  const s = battle.value;
  if (!s || s.phase !== "question" || s.monster.rank === "boss") return;
  const h = hero.value;
  const cost = Math.min(FLEE_COST, h.gold);
  hero.value = { ...h, gold: h.gold - cost, streak: 0 };
  battle.value = {
    ...s,
    phase: "fled",
    streak: 0,
    rewards: { xp: 0, gold: -cost, leveledUp: false, newLevel: h.lvl },
  };
}

export function endBattle(): void {
  const s = battle.value;
  if (!s) return;
  lastResult = {
    won: s.phase === "won",
    fled: s.phase === "fled",
    monsterId: s.monster.id,
    chestId: s.chestId,
  };
  battle.value = null;
}
